/**
 * Что делать с тем, что менеджер набрал в поиске.
 *
 * ⚠️ ДВА ФИЛЬТРА ВЕДУТ СЕБЯ ПО-РАЗНОМУ (§4): имя ищется ПОДСТРОКОЙ по любому написанию, телефон —
 * ТОЛЬКО ЦЕЛИКОМ, потому что номера в базе зашифрованы и поиск по части технически невозможен.
 * Половина номера возвращает пустой список БЕЗ ошибки, то есть выглядит ровно как «такого гостя
 * нет», — и спека требует сказать об этом ДО поиска, иначе менеджер решит, что база пуста. Поэтому
 * недобранный номер — отдельное состояние: запрос по нему вообще не уходит.
 *
 * Поле ввода одно, а фильтра два, поэтому вид запроса выводится из НАБРАННОГО: угадывание здесь
 * безопасно (имён из одних цифр и знаков набора не бывает), а два инпута заставили бы менеджера
 * объявлять то, что видно по самой строке.
 */
export type ClientSearch =
  | { kind: 'all' }
  | { kind: 'name'; name: string }
  | { kind: 'phone'; phone: string }
  /** Похоже на номер, но он не дособран — искать нечем, и это надо сказать, а не искать. */
  | { kind: 'phonePartial' }

/** Только то, из чего набирают номер: цифры и знаки разбивки. Буква сразу делает строку именем. */
const PHONE_INPUT = /^[\d+\s()\-.]+$/

export function clientSearch(input: string): ClientSearch {
  const trimmed = input.trim()
  if (trimmed === '') return { kind: 'all' }
  if (!PHONE_INPUT.test(trimmed)) return { kind: 'name', name: trimmed }

  const digits = trimmed.replace(/\D/g, '')
  // Одни скобки и плюс — ещё не попытка набрать номер; ищем как имя, чтобы поле не «залипало» в
  // состоянии «дособерите номер» на пустой по сути строке.
  if (digits === '') return { kind: 'name', name: trimmed }

  const phone = toE164(trimmed, digits)
  return phone ? { kind: 'phone', phone } : { kind: 'phonePartial' }
}

/**
 * Приводим к виду, в каком номер лежит на сервере (E.164), — иначе точное сравнение не совпадёт.
 *
 * ⚠️ ВЕТКА «ПЛЮС» СТОИТ ПЕРВОЙ, И ЭТО НЕ ПОРЯДОК РАДИ ПОРЯДКА. Поставив «+», человек уже назвал
 * страну, и переписывать номер по российским правилам нельзя: вьетнамский `+84 91 234 5678` и
 * токийский `+81 3 1234 5678` — по одиннадцать цифр, начинающихся с восьмёрки, то есть под правило
 * «8XXXXXXXXXX → +7XXXXXXXXXX» подходят идеально. Проверь их вторыми — и поиск ушёл бы по ЧУЖОМУ
 * московскому номеру, а менеджер прочитал бы «такого номера нет» про номер, который никто не искал.
 *
 * Без «плюса» разбираем только российский в трёх привычных написаниях: длина номера у стран разная,
 * и угадывать полноту нельзя.
 */
function toE164(raw: string, digits: string): string | null {
  if (raw.startsWith('+')) return digits.length >= 11 ? `+${digits}` : null
  if (/^[78]\d{10}$/.test(digits)) return `+7${digits.slice(1)}`
  // Мобильный без кода страны: 9XXXXXXXXX — так его чаще всего и диктуют.
  if (/^9\d{9}$/.test(digits)) return `+7${digits}`
  return null
}

/**
 * Параметры ручки. Пустой объект — вся база: сервер сам вернёт первую страницу.
 *
 * ⚠️ У `phonePartial` параметров тоже нет, то есть по ним он неотличим от «всей базы». Всю базу он
 * не покажет — запрос под ним выключен (`clientsQuery`), а экран разбирает это состояние первым, —
 * но связка неявная: уйдёт `enabled`, и недобранный номер начнёт отвечать всей базой.
 */
export function clientSearchParams(search: ClientSearch): { name?: string; phone?: string } {
  if (search.kind === 'name') return { name: search.name }
  if (search.kind === 'phone') return { phone: search.phone }
  return {}
}
